import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { useBox } from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { DollarSign, TrendingUp, Wallet, AlertCircle, BarChart3, FileText, CheckCircle, ShoppingCart, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import CashCounter from "../components/CashCounter";
import LiveReport from "../components/LiveReport";
import DashboardAnalytics from "../components/DashboardAnalytics";
import QuickActionsCard from "../components/QuickActionsCard";
import { CashOutlayDialog } from "../components/CashOutlayDialog";
import { todayCR } from "../utils/dateHelpers";
import { getErrorMessage } from "../utils/errorHandler";
import { calculateExpectedCash } from "../utils/cashCalculations";

// Helper function to fetch current datetime from server in Costa Rica timezone
const fetchCostaRicaDateTime = async () => {
  try {
    const response = await axiosInstance.get('/current-time-cr');
    return {
      datetime_local: response.data.datetime_local,  // For datetime-local input (24h format)
      formatted: response.data.formatted  // For display (12h format with AM/PM)
    };
  } catch (error) {
    console.error("Error fetching CR time:", error);
    // Fallback to local time if server fails
    const now = new Date();
    return {
      datetime_local: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
      formatted: now.toLocaleString('es-CR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: true 
      })
    };
  }
};

// Helper function to format datetime-local input value to readable format (12h)
const formatDateTime12h = (datetimeLocalValue) => {
  if (!datetimeLocalValue) return '';
  const date = new Date(datetimeLocalValue);
  return date.toLocaleString('es-CR', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric', 
    hour: '2-digit', 
    minute: '2-digit', 
    hour12: true 
  });
};

export default function Dashboard({ user }) {
  const { selectedBox } = useBox();
  const navigate = useNavigate();
  const [cashRegister, setCashRegister] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOutlayDialog, setShowOutlayDialog] = useState(false);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showCancelOpenDialog, setShowCancelOpenDialog] = useState(false);
  const [openDialogKey, setOpenDialogKey] = useState(0);
  const [closeDialogKey, setCloseDialogKey] = useState(0);
  const [showLiveReport, setShowLiveReport] = useState(true);
  const [previousClose, setPreviousClose] = useState(null);
  const pollingIntervalRef = useRef(null);
  const [pendingVentas, setPendingVentas] = useState(null);  // Pending values from backend
  const [discountRequests, setDiscountRequests] = useState([]);
  const discountPollRef = useRef(null);
  const isMaster = user?.role === "master";
  
  const [openData, setOpenData] = useState({
    initial_cash_colones: 0,
    initial_cash_dollars: 0,
    exchange_rate: 550,
    opened_at: "",  // Fecha/hora en formato datetime-local (24h)
    opened_at_display: ""  // Fecha/hora en formato legible (12h)
  });
  const [nextTurno, setNextTurno] = useState(1);  // Track which turno will be opened
  
  const [closeData, setCloseData] = useState({
    final_cash_colones: 0,
    final_cash_dollars: 0,
    commission_colones: 0,  // Commission amount in colones
    commission_dollars: 0,  // Commission amount in dollars
    saldo_api_colones: 0,  // NEW: SALDO API (informational only)
    saldo_api_dollars: 0,
    saldo_tiro_colones: 0,  // NEW: SALDO TIRO (informational only)
    saldo_tiro_dollars: 0,
    password: "",
    closed_at: "",  // Fecha/hora en formato datetime-local (24h)
    closed_at_display: "",  // Fecha/hora en formato legible (12h)
    close_both_shifts: false  // MASTER option to close T1+T2 together
  });

  const handleColonesOpenTotal = (total) => {
    setOpenData(prev => ({ ...prev, initial_cash_colones: total }));
  };

  const handleDollarsOpenTotal = (total) => {
    setOpenData(prev => ({ ...prev, initial_cash_dollars: total }));
  };

  const handleColonesCloseTotal = (total) => {
    setCloseData(prev => ({ ...prev, final_cash_colones: total }));
  };

  const handleDollarsCloseTotal = (total) => {
    setCloseData(prev => ({ ...prev, final_cash_dollars: total }));
  };

  // Función para anular apertura de caja (MASTER only)
  const handleCancelOpening = async () => {
    try {
      await axiosInstance.post(`/cash-register/cancel-opening?register_id=${cashRegister.id}`);
      toast.success("Apertura anulada. El cajero puede volver a abrir la caja correctamente.");
      setShowCancelOpenDialog(false);
      fetchCashRegister();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al anular apertura");
    }
  };

  // Calculate expected cash using centralized utility
  const getExpectedCash = () => {
    if (!cashRegister) return { expectedColones: 0, expectedDollars: 0 };
    return calculateExpectedCash(cashRegister, pendingVentas);
  };

  // Fetch pending values from backend
  const fetchPendingValues = async () => {
    if (!selectedBox) return;
    try {
      const response = await axiosInstance.get(`/pending-values?box_id=${selectedBox.id}`);
      if (response.data.success && response.data.pending_values) {
        setPendingVentas(response.data.pending_values);
      }
    } catch (error) {
      console.error("Error fetching pending values:", error);
    }
  };

  const fetchCashRegister = async () => {
    if (!selectedBox) {
      setLoading(false);
      return;
    }
    
    try {
      console.log(`[Dashboard] Fetching cash register for box: ${selectedBox.name} (${selectedBox.id})`);
      const response = await axiosInstance.get(`/cash-register/current?box_id=${selectedBox.id}`);
      console.log(`[Dashboard] Cash register response:`, response.data);
      setCashRegister(response.data);
      
      if (!response.data) {
        console.log(`[Dashboard] No open register found for ${selectedBox.name}`);
      }
    } catch (error) {
      console.error(`[Dashboard] Error fetching cash register for ${selectedBox.name}:`, error);
      setCashRegister(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchCashRegister();
    fetchPendingValues();  // Fetch pending values from backend
    pollingIntervalRef.current = setInterval(() => {
      fetchCashRegister();
      fetchPendingValues();
    }, 5000); // Update every 5 seconds
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [selectedBox]);

  // Discount requests polling for MASTER
  const fetchDiscountRequests = async () => {
    if (!selectedBox || !isMaster) return;
    try {
      const resp = await axiosInstance.get(`/inventory/discount-requests?box_id=${selectedBox.id}&status=pending`);
      setDiscountRequests(resp.data.requests || []);
    } catch { /* ignored */ }
  };

  useEffect(() => {
    if (isMaster && selectedBox) {
      fetchDiscountRequests();
      discountPollRef.current = setInterval(fetchDiscountRequests, 4000);
      return () => { if (discountPollRef.current) clearInterval(discountPollRef.current); };
    }
  }, [selectedBox, isMaster]);

  const [approvePercentage, setApprovePercentage] = useState("");

  // Fiscal deadlines (TribuCR)
  const [taxDeadlines, setTaxDeadlines] = useState([]);
  const [dismissedDeadlines, setDismissedDeadlines] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tax_deadlines_dismissed") || "[]"); } catch (e) { console.warn(e); return []; }
  });
  useEffect(() => {
    if (!isMaster) return;
    axiosInstance.get("/tributary/deadlines").then((r) => setTaxDeadlines(r.data.deadlines || [])).catch(() => setTaxDeadlines([]));
  }, [isMaster, selectedBox]);
  const dismissDeadline = (key) => {
    const nd = [...dismissedDeadlines, key];
    setDismissedDeadlines(nd);
    localStorage.setItem("tax_deadlines_dismissed", JSON.stringify(nd));
  };
  // Only show deadlines within 30 days (or overdue up to 60 days), and not dismissed
  const visibleDeadlines = taxDeadlines.filter((d) => {
    const key = `${d.box_id}_${d.due_date}`;
    return !dismissedDeadlines.includes(key) && d.days_left <= 30 && d.days_left >= -60;
  });

  const handleApproveDiscount = async (req) => {
    const pct = approvePercentage ? parseFloat(approvePercentage) : req.percentage;
    if (pct <= 0 || pct > 100) { toast.error("Porcentaje inválido"); return; }
    try {
      await axiosInstance.post(`/inventory/discount-requests/${req.id}/approve`, { percentage: pct });
      toast.success(`Descuento de ${pct}% aprobado para ${req.requested_by}`);
      setApprovePercentage("");
      fetchDiscountRequests();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al aprobar"));
    }
  };

  const handleRejectDiscount = async (req) => {
    try {
      await axiosInstance.post(`/inventory/discount-requests/${req.id}/reject`);
      toast.info("Solicitud rechazada");
      fetchDiscountRequests();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al rechazar"));
    }
  };

  const handleOpenCashRegister = async () => {
    try {
      // Prepare data - only include opened_at if provided
      const dataToSend = { 
        box_id: selectedBox.id,
        initial_cash_colones: openData.initial_cash_colones,
        initial_cash_dollars: openData.initial_cash_dollars,
        exchange_rate: openData.exchange_rate
      };
      
      // Add opened_at only if user provided a datetime
      if (openData.opened_at) {
        // Convert to ISO format (backend expects ISO string)
        dataToSend.opened_at = new Date(openData.opened_at).toISOString();
      }
      
      console.log('Opening cash register with data:', dataToSend);
      const response = await axiosInstance.post('/cash-register/open', dataToSend);
      setCashRegister(response.data);
      setShowOpenDialog(false);
      setPreviousClose(null);
      
      // Show turno information
      const turnoText = response.data.turno === 1 ? "Turno 1" : "Turno 2";
      toast.success(`Caja abierta exitosamente - ${turnoText}`);
      
      // NOTE: Expense reminders are now shown on system entry (handled in Layout.js)
      // to ensure the user is informed even if the cash register is never opened.
      
      if (response.data.previous_close) {
        const prev = response.data.previous_close;
        const prevCashColones = prev.cash_after_commission_colones || prev.final_cash_colones || 0;
        const prevCashDollars = prev.cash_after_commission_dollars || prev.final_cash_dollars || 0;
        const diff_colones = openData.initial_cash_colones - prevCashColones;
        const diff_dollars = openData.initial_cash_dollars - prevCashDollars;
        
        if (Math.abs(diff_colones) > 0.01 || Math.abs(diff_dollars) > 0.01) {
          const diffText = `Diferencia: ₡${diff_colones.toFixed(2)} / $${diff_dollars.toFixed(2)}`;
          if (diff_colones > 0 || diff_dollars > 0) {
            toast.success(`${diffText} (Sobrante)`);
          } else {
            toast.error(`${diffText} (Faltante)`);
          }
        } else {
          toast.success("Efectivo cuadra correctamente");
        }
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al abrir caja"));
    }
  };

  // Fetch previous close when opening dialog
  const handleOpenDialogClick = async () => {
    if (!selectedBox) {
      toast.error("Selecciona una caja primero");
      return;
    }
    
    // Fetch current Costa Rica datetime from server
    const crTime = await fetchCostaRicaDateTime();
    
    try {
      // Get last closed register for this box
      const response = await axiosInstance.get(`/cash-register/history?box_id=${selectedBox.id}`);
      if (response.data && response.data.length > 0) {
        const lastClose = response.data[0]; // Most recent closed register
        setPreviousClose(lastClose);
        
        // Determine next turno based on today's registers
        const today = todayCR();
        const todayRegisters = response.data.filter(r => {
          const registerDate = r.opened_at ? r.opened_at.split('T')[0] : '';
          return registerDate === today;
        });
        
        if (todayRegisters.length >= 2) {
          toast.error("Ya se realizaron 2 aperturas hoy. Máximo 2 turnos por día.");
          return;
        }
        setNextTurno(todayRegisters.length + 1);
        
        // P1 FIX: Pre-fill initial cash with cash_after_commission from last close
        const suggestedColones = lastClose.cash_after_commission_colones ?? lastClose.final_cash_colones ?? 0;
        const suggestedDollars = lastClose.cash_after_commission_dollars ?? lastClose.final_cash_dollars ?? 0;
        
        setOpenData(prev => ({
          ...prev,
          initial_cash_colones: suggestedColones,
          initial_cash_dollars: suggestedDollars,
          opened_at: crTime.datetime_local,
          opened_at_display: crTime.formatted
        }));
      } else {
        setPreviousClose(null);
        setNextTurno(1);
        setOpenData(prev => ({
          ...prev,
          initial_cash_colones: 0,
          initial_cash_dollars: 0,
          opened_at: crTime.datetime_local,
          opened_at_display: crTime.formatted
        }));
      }
    } catch (error) {
      console.error("Error fetching previous close:", error);
      setNextTurno(1);
      setOpenData(prev => ({
        ...prev,
        opened_at: crTime.datetime_local,
        opened_at_display: crTime.formatted
      }));
    }
    
    setShowOpenDialog(true);
    setOpenDialogKey(prev => prev + 1);
  };

  const handleCloseCashRegister = async () => {
    try {
      // If cajero, save prepared close data
      if (user.role === 'cajero') {
        await axiosInstance.post(`/cash-register/prepare-close?box_id=${selectedBox.id}&final_cash_colones=${closeData.final_cash_colones}&final_cash_dollars=${closeData.final_cash_dollars}`);
        toast.success("Conteo guardado. PALISTORE revisará y cerrará la caja.");
        setShowCloseDialog(false);
        setCloseData({ final_cash_colones: 0, final_cash_dollars: 0, commission_colones: 0, commission_dollars: 0, saldo_api_colones: 0, saldo_api_dollars: 0, saldo_tiro_colones: 0, saldo_tiro_dollars: 0, password: "", closed_at: "", closed_at_display: "", close_both_shifts: false });
        fetchCashRegister(); // Refresh to show prepared data
        return;
      }
      
      // If master, close the register
      const dataToSend = { 
        box_id: selectedBox.id,
        final_cash_colones: closeData.final_cash_colones,
        final_cash_dollars: closeData.final_cash_dollars,
        commission_colones: closeData.commission_colones,
        commission_dollars: closeData.commission_dollars,
        saldo_api_colones: closeData.saldo_api_colones,  // NEW: Informational only
        saldo_api_dollars: closeData.saldo_api_dollars,
        saldo_tiro_colones: closeData.saldo_tiro_colones,  // NEW: Informational only
        saldo_tiro_dollars: closeData.saldo_tiro_dollars,
        password: closeData.password,
        close_both_shifts: closeData.close_both_shifts  // MASTER option for T1+T2 close
      };
      
      // Add closed_at only if user provided a datetime
      // IMPORTANT: Send the datetime as-is without converting to UTC
      // This preserves the Costa Rica local time that the user selected
      if (closeData.closed_at) {
        // closeData.closed_at is in format "YYYY-MM-DDTHH:mm" (datetime-local)
        // Send it directly - backend will interpret it as Costa Rica time
        dataToSend.closed_at = closeData.closed_at;
      }
      
      const response = await axiosInstance.post('/cash-register/close', dataToSend);
      
      // Check if PDF was generated
      if (response.data.pdf_generated && response.data.pdf_base64) {
        // Convert base64 to blob and download
        const pdfBlob = base64ToBlob(response.data.pdf_base64, 'application/pdf');
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', response.data.pdf_filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success("Caja cerrada exitosamente. PDF descargado automáticamente.");
      } else {
        toast.success("Caja cerrada exitosamente");
        if (response.data.pdf_error) {
          toast.warning("Error al generar PDF: " + response.data.pdf_error);
        }
      }
      
      // Immediately clear state and pause polling to prevent stale data
      setCashRegister(null);
      setShowCloseDialog(false);
      setCloseData({ final_cash_colones: 0, final_cash_dollars: 0, commission_colones: 0, commission_dollars: 0, password: "", closed_at: "", closed_at_display: "", close_both_shifts: false });
      
      // Pause polling briefly, then restart to confirm the register is truly closed
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      setTimeout(() => {
        fetchCashRegister();
        pollingIntervalRef.current = setInterval(fetchCashRegister, 5000);
      }, 1000);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al cerrar caja"));
    }
  };
  
  // Helper function to convert base64 to blob
  const base64ToBlob = (base64, type) => {
    const byteCharacters = atob(base64);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type });
  };

  const handleDownloadPDF = async () => {
    try {
      toast.info("Generando PDF con tipo de cambio del BCCR...");
      
      const response = await axiosInstance.get(`/cash-register/daily-report-pdf?box_id=${selectedBox.id}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_diario_${todayCR()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF descargado exitosamente");
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al generar PDF"));
    }
  };

  const formatCurrency = (amount, currency = "colones") => {
    const symbol = currency === "colones" ? "₡" : "$";
    return `${symbol}${amount.toFixed(2)}`;
  };
  
  const handleFixTodayDates = async () => {
    if (!window.confirm("¿Corregir fechas de movimientos de hoy? Esto actualizará las transacciones, cuentas y cajas abiertas hoy.")) {
      return;
    }
    
    try {
      const response = await axiosInstance.post("/fix-today-dates");
      toast.success(`Fechas corregidas: ${response.data.transactions_updated} transacciones, ${response.data.debts_updated} cuentas, ${response.data.registers_updated} cajas`);
      fetchCashRegister(); // Refresh data
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al corregir fechas"));
    }
  };

  if (loading) {
    return <div className="text-center py-20">Cargando...</div>;
  }

  if (!selectedBox) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-600">Selecciona una caja para comenzar</p>
      </div>
    );
  }

  if (!cashRegister) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-xl">
          <CardHeader>
            <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              No hay caja abierta - {selectedBox.name}
            </CardTitle>
            <CardDescription className="text-sm">Abre esta caja para comenzar a operar</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              data-testid="open-cash-register-button"
              onClick={async () => {
                setOpenData({ initial_cash_colones: 0, initial_cash_dollars: 0, exchange_rate: 550, opened_at: "", opened_at_display: "" });
                await handleOpenDialogClick();
              }}
              className="w-full py-6 bg-blue-600 hover:bg-blue-700"
            >
              Abrir Caja
            </Button>
          </CardContent>
        </Card>

        <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
          <DialogContent data-testid="open-cash-dialog" className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                Abrir Caja - Conteo de Efectivo
                <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
                  nextTurno === 1 
                    ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                    : 'bg-amber-100 text-amber-700 border border-amber-300'
                }`}>
                  {nextTurno === 1 ? '☀️ Turno Mañana' : '🌙 Turno Tarde'}
                </span>
              </DialogTitle>
              <DialogDescription>Cuenta los billetes y monedas para la apertura</DialogDescription>
            </DialogHeader>
            
            <Tabs defaultValue="colones" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="colones">Colones (₡)</TabsTrigger>
                <TabsTrigger value="dollars">Dólares ($)</TabsTrigger>
              </TabsList>
              
              <TabsContent value="colones" className="mt-4">
                <CashCounter key={`open-colones-${openDialogKey}`} currency="colones" onTotalChange={handleColonesOpenTotal} />
              </TabsContent>
              
              <TabsContent value="dollars" className="mt-4">
                <CashCounter key={`open-dollars-${openDialogKey}`} currency="dollars" onTotalChange={handleDollarsOpenTotal} />
              </TabsContent>
            </Tabs>

            <div className="space-y-3 pt-4 border-t">
              <div>
                <Label htmlFor="exchange_rate">Tipo de Cambio (₡ por $1)</Label>
                <Input
                  id="exchange_rate"
                  data-testid="exchange-rate-input"
                  type="number"
                  step="0.01"
                  value={openData.exchange_rate}
                  onChange={(e) => setOpenData({...openData, exchange_rate: parseFloat(e.target.value) || 550})}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="opened_at_display">Fecha y Hora de Apertura</Label>
                <Input
                  id="opened_at_display"
                  type="text"
                  value={openData.opened_at_display}
                  className="mt-1.5 font-semibold text-lg"
                  readOnly={user?.role !== "master"}
                  onChange={async (e) => {
                    if (user?.role === "master") {
                      // Allow master to edit - but this is just display
                      // We'll keep using datetime-local for the actual value
                      setOpenData({...openData, opened_at_display: e.target.value});
                    }
                  }}
                />
                {user?.role === "master" && (
                  <div className="mt-2">
                    <Label htmlFor="opened_at" className="text-xs text-slate-600">Ajuste fino (formato 24h)</Label>
                    <Input
                      id="opened_at"
                      type="datetime-local"
                      value={openData.opened_at}
                      onChange={(e) => {
                        const newDatetimeLocal = e.target.value;
                        const formatted = formatDateTime12h(newDatetimeLocal);
                        setOpenData({
                          ...openData, 
                          opened_at: newDatetimeLocal,
                          opened_at_display: formatted
                        });
                      }}
                      className="mt-1"
                    />
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  {user?.role === "master" 
                    ? "Hora actual de Costa Rica (solo Master puede modificar)" 
                    : "Hora actual de Costa Rica (solo Master puede modificar)"}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-100 rounded-lg">
                <div>
                  <p className="text-sm text-slate-600">Total Colones</p>
                  <p className="text-xl font-bold text-blue-600" data-testid="open-total-colones">
                    ₡{openData.initial_cash_colones.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total Dólares</p>
                  <p className="text-xl font-bold text-green-600" data-testid="open-total-dollars">
                    ${openData.initial_cash_dollars.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Show difference with previous close */}
              {previousClose && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 mb-2">Comparación con Cierre Anterior</h4>
                  <div className="space-y-2 text-sm">
                    {(previousClose.commission_colones > 0 || previousClose.commission_dollars > 0) && (
                      <div className="flex justify-between text-xs text-yellow-600">
                        <span>Comisión deducida:</span>
                        <span>₡{(previousClose.commission_colones || 0).toFixed(2)} / ${(previousClose.commission_dollars || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {/* Show previous closure final cash - what was actually left */}
                    <div className="flex justify-between">
                      <span className="text-yellow-700">Efectivo Final Cierre Anterior:</span>
                      <span className="font-bold text-blue-600">
                        ₡{(previousClose.cash_after_commission_colones || previousClose.final_cash_colones || 0).toFixed(2)} / ${(previousClose.cash_after_commission_dollars || previousClose.final_cash_dollars || 0).toFixed(2)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between mt-2 pt-2 border-t border-yellow-200">
                      <span className="text-yellow-700">Efectivo Inicial Actual:</span>
                      <span className="font-semibold">
                        ₡{openData.initial_cash_colones.toFixed(2)} / ${openData.initial_cash_dollars.toFixed(2)}
                      </span>
                    </div>
                    
                    {/* Show difference */}
                    {(() => {
                      const prevColones = previousClose.cash_after_commission_colones || previousClose.final_cash_colones || 0;
                      const prevDollars = previousClose.cash_after_commission_dollars || previousClose.final_cash_dollars || 0;
                      const diffColones = openData.initial_cash_colones - prevColones;
                      const diffDollars = openData.initial_cash_dollars - prevDollars;
                      const hasDiff = Math.abs(diffColones) > 0.01 || Math.abs(diffDollars) > 0.01;
                      
                      return hasDiff ? (
                        <div className="flex justify-between mt-2 pt-2 border-t border-yellow-300">
                          <span className="text-yellow-800 font-semibold">Diferencia:</span>
                          <span className={`font-bold ${diffColones >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {diffColones >= 0 ? '+' : ''}₡{diffColones.toFixed(2)} / {diffDollars >= 0 ? '+' : ''}${diffDollars.toFixed(2)}
                          </span>
                        </div>
                      ) : (
                        <div className="flex justify-between mt-2 pt-2 border-t border-yellow-300">
                          <span className="text-yellow-800 font-semibold">Diferencia:</span>
                          <span className="font-bold text-green-600">✓ Sin diferencia</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowOpenDialog(false)}>
                Cancelar
              </Button>
              <Button
                data-testid="confirm-open-cash-button"
                onClick={handleOpenCashRegister}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Abrir Caja
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Calculate difference from previous close (if exists)
  // Use cash_after_commission if available (new), otherwise use final_cash (old data)
  const difference_colones = previousClose 
    ? cashRegister.initial_cash_colones - (previousClose.cash_after_commission_colones || previousClose.final_cash_colones)
    : 0;
  const difference_dollars = previousClose 
    ? cashRegister.initial_cash_dollars - (previousClose.cash_after_commission_dollars || previousClose.final_cash_dollars)
    : 0;

  return (
    <div className="space-y-3">
      {/* Alertas de vencimientos fiscales (TribuCR) */}
      {visibleDeadlines.length > 0 && (
        <div className="space-y-1.5" data-testid="tax-deadline-alerts">
          {visibleDeadlines.map((d) => {
            const key = `${d.box_id}_${d.due_date}`;
            const overdue = d.is_overdue;
            const urgent = !overdue && d.days_left <= 7;
            return (
              <div
                key={key}
                className={
                  "flex items-center justify-between gap-3 rounded-lg border-2 px-3 py-2 shadow-sm " +
                  (overdue
                    ? "bg-red-50 border-red-400 text-red-900"
                    : urgent
                    ? "bg-orange-50 border-orange-400 text-orange-900"
                    : "bg-amber-50 border-amber-300 text-amber-900")
                }
                data-testid={`tax-deadline-${d.box_id}`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-2xl">{overdue ? "🚨" : urgent ? "⏰" : "📅"}</span>
                  <div className="text-sm min-w-0">
                    <div className="font-bold truncate">
                      {overdue
                        ? `¡VENCIDO hace ${Math.abs(d.days_left)} día(s)!`
                        : d.days_left === 0
                        ? "¡Vence HOY!"
                        : `Faltan ${d.days_left} día(s)`}
                      {" — "}Formulario <b>{d.form_code}</b>
                    </div>
                    <div className="text-xs opacity-90">
                      Caja <b>{d.box_name}</b> ({d.regime}) · {d.period_label} · Vence {d.due_date}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded font-semibold flex items-center gap-1"
                    onClick={() => {
                      const label = overdue
                        ? `⚠️ VENCIDO hace ${Math.abs(d.days_left)} día(s)`
                        : d.days_left === 0
                        ? "⏰ Vence HOY"
                        : `📅 Faltan ${d.days_left} día(s)`;
                      const msg = `${label}\n\n🧾 *Recordatorio Fiscal Hacienda CR*\nFormulario: *${d.form_code}*\nRégimen: ${d.regime}\nPeríodo: ${d.period_label}\nCaja: ${d.box_name}\nFecha límite: *${d.due_date}*\n\nEnviado desde PaliStore`;
                      const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
                      window.open(url, "_blank");
                    }}
                    title="Compartir por WhatsApp"
                    data-testid={`tax-deadline-share-${d.box_id}`}
                  >
                    📱 WhatsApp
                  </button>
                  <button
                    className="text-xs opacity-70 hover:opacity-100 underline shrink-0"
                    onClick={() => dismissDeadline(key)}
                    data-testid={`tax-deadline-dismiss-${d.box_id}`}
                  >
                    Ocultar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Dashboard
              {cashRegister.turno && (
                <span className={`ml-3 px-3 py-1 text-sm font-semibold rounded-full ${
                  cashRegister.turno === 1 
                    ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                    : 'bg-amber-100 text-amber-700 border border-amber-300'
                }`}>
                  {cashRegister.turno === 1 ? '☀️ Turno Mañana' : '🌙 Turno Tarde'}
                </span>
              )}
            </h1>
            <p className="text-sm text-slate-600">
              Caja abierta por {cashRegister.opened_by}
              {cashRegister.status === 'preparada' && cashRegister.prepared_by && (
                <span className="ml-2 px-2 py-0.5 text-xs font-semibold text-orange-700 bg-orange-100 rounded-full border border-orange-300">
                  ⏳ Cierre preparado por {cashRegister.prepared_by} - Pendiente finalización
                </span>
              )}
            </p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {(selectedBox?.enabled_modules ? selectedBox.enabled_modules.inventory !== false : true) && (
            <Button
              data-testid="dashboard-sell-inventory-button"
              onClick={() => navigate('/inventory?tab=vender')}
              size="sm"
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              title="Ir a vender productos del inventario"
            >
              <ShoppingCart className="w-4 h-4" />
              Vender
            </Button>
          )}
          <Button
            data-testid="toggle-live-report-button"
            onClick={() => setShowLiveReport(!showLiveReport)}
            variant="outline"
            size="sm"
            className="gap-2 hidden md:inline-flex"
          >
            <BarChart3 className="w-4 h-4" />
            {showLiveReport ? 'Ocultar' : 'Ver'} Reporte
          </Button>
          
          {user.role === 'master' && (
            <>
              <Button
                onClick={handleFixTodayDates}
                variant="outline"
                size="sm"
                className="gap-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300 hidden md:inline-flex"
                title="Corregir fechas de hoy"
              >
                📅 Corregir Fechas
              </Button>
              
              <Button
                data-testid="download-pdf-button"
                onClick={handleDownloadPDF}
                variant="outline"
                size="sm"
                className="gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300 hidden md:inline-flex"
              >
                <FileText className="w-4 h-4" />
                Descargar PDF
              </Button>
            </>
          )}
          
          <Button
            onClick={() => setShowOutlayDialog(true)}
            variant="outline"
            size="sm"
            className="gap-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-300 hidden md:inline-flex"
            data-testid="cash-outlay-button"
            title="Registrar salida de efectivo con calculadora de billetes"
          >
            💸 Salida Efectivo
          </Button>

          <Button
            data-testid="close-cash-register-button"
            onClick={async () => {
              // Fetch current Costa Rica datetime from server
              const crTime = await fetchCostaRicaDateTime();
              
              // If master and there's prepared data, pre-fill it
              if (user.role === 'master' && cashRegister.prepared_close_colones != null) {
                // Use prepared_at date if available, otherwise use current time
                let closeDatetime, closeDatetimeDisplay;
                if (cashRegister.prepared_at) {
                  // Use the date when cashier prepared the close
                  const preparedDate = new Date(cashRegister.prepared_at);
                  // Format date for datetime-local input (YYYY-MM-DDTHH:mm)
                  const year = preparedDate.getFullYear();
                  const month = String(preparedDate.getMonth() + 1).padStart(2, '0');
                  const day = String(preparedDate.getDate()).padStart(2, '0');
                  const hours = String(preparedDate.getHours()).padStart(2, '0');
                  const minutes = String(preparedDate.getMinutes()).padStart(2, '0');
                  closeDatetime = `${year}-${month}-${day}T${hours}:${minutes}`;
                  
                  // Format for display
                  closeDatetimeDisplay = preparedDate.toLocaleString('es-CR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  });
                } else {
                  // Fallback to current time
                  closeDatetime = crTime.datetime_local;
                  closeDatetimeDisplay = crTime.formatted;
                }
                
                setCloseData({ 
                  final_cash_colones: cashRegister.prepared_close_colones, 
                  final_cash_dollars: cashRegister.prepared_close_dollars || 0,
                  commission_colones: 0,
                  commission_dollars: 0,
                  saldo_api_colones: 0,
                  saldo_api_dollars: 0,
                  saldo_tiro_colones: 0,
                  saldo_tiro_dollars: 0, 
                  password: "",
                  closed_at: closeDatetime,
                  closed_at_display: closeDatetimeDisplay,
                  close_both_shifts: false
                });
              } else {
                setCloseData({ 
                  final_cash_colones: 0, 
                  final_cash_dollars: 0,
                  commission_colones: 0,
                  commission_dollars: 0,
                  saldo_api_colones: 0,
                  saldo_api_dollars: 0,
                  saldo_tiro_colones: 0,
                  saldo_tiro_dollars: 0, 
                  password: "",
                  closed_at: crTime.datetime_local,
                  closed_at_display: crTime.formatted,
                  close_both_shifts: false
                });
              }
              setCloseDialogKey(prev => prev + 1);
              setShowCloseDialog(true);
            }}
            variant="destructive"
            size="sm"
            className="bg-red-600 hover:bg-red-700"
          >
            {user.role === 'master' ? (
              cashRegister.status === 'preparada' ? '⏳ Finalizar Cierre Pendiente' : 
              cashRegister.prepared_by ? '✓ Revisar y Cerrar Caja' : 'Cerrar Caja'
            ) : (
              'Preparar Cierre'
            )}
          </Button>
          
          {/* Botón para anular apertura - solo MASTER */}
          {user.role === 'master' && cashRegister.status === 'abierta' && (
            <Button
              onClick={() => setShowCancelOpenDialog(true)}
              variant="outline"
              size="sm"
              className="border-orange-500 text-orange-600 hover:bg-orange-50 hidden md:inline-flex"
            >
              ❌ Anular Apertura
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1 md:hidden" data-testid="dashboard-more-button">
                <MoreHorizontal className="w-4 h-4" />
                Más
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setShowLiveReport(!showLiveReport)} data-testid="more-toggle-report">
                <BarChart3 className="w-4 h-4 mr-2" />
                {showLiveReport ? 'Ocultar' : 'Ver'} Reporte
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowOutlayDialog(true)} data-testid="more-cash-outlay">
                💸 <span className="ml-2">Salida Efectivo</span>
              </DropdownMenuItem>
              {user.role === 'master' && (
                <>
                  <DropdownMenuItem onClick={handleDownloadPDF} data-testid="more-download-pdf">
                    <FileText className="w-4 h-4 mr-2" />
                    Descargar PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleFixTodayDates} data-testid="more-fix-dates">
                    📅 <span className="ml-2">Corregir Fechas</span>
                  </DropdownMenuItem>
                  {cashRegister.status === 'abierta' && (
                    <DropdownMenuItem onClick={() => setShowCancelOpenDialog(true)} className="text-orange-600" data-testid="more-cancel-open">
                      ❌ <span className="ml-2">Anular Apertura</span>
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              Efectivo Caja (₡)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold" data-testid="cash-colones-amount">
              {formatCurrency(cashRegister.total_cash_colones)}
            </div>
            <p className="text-xs mt-1 opacity-90">
              Inicial: {formatCurrency(cashRegister.initial_cash_colones)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Efectivo Caja ($)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold" data-testid="cash-dollars-amount">
              {formatCurrency(cashRegister.total_cash_dollars, "dollars")}
            </div>
            <p className="text-xs mt-1 opacity-90">
              Inicial: {formatCurrency(cashRegister.initial_cash_dollars, "dollars")}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Ventas Totales (₡)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold" data-testid="sales-colones-amount">
              {formatCurrency(cashRegister.total_sales_colones)}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Ventas Totales ($)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold" data-testid="sales-dollars-amount">
              {formatCurrency(cashRegister.total_sales_dollars, "dollars")}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Diferencia desde Apertura
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-600">Diferencia en Colones</p>
                <p className={`text-xl font-bold ${difference_colones >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {difference_colones >= 0 ? '+' : ''}{formatCurrency(difference_colones)}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Diferencia en Dólares</p>
                <p className={`text-xl font-bold ${difference_dollars >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {difference_dollars >= 0 ? '+' : ''}{formatCurrency(difference_dollars, "dollars")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <QuickActionsCard
          cashRegister={cashRegister}
          selectedBox={selectedBox}
          onTransactionCreated={() => {
            fetchCashRegister();
            fetchPendingValues();
          }}
        />
      </div>

      {/* Live Report */}
      {/* Discount Requests for MASTER */}
      {isMaster && discountRequests.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 shadow-lg animate-pulse-once">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-700 flex items-center gap-2 text-lg">
              <AlertCircle className="w-5 h-5" />
              Solicitudes de Descuento Pendientes ({discountRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {discountRequests.map(req => {
              const descMonto = Math.round((req.cart_total || 0) * req.percentage / 100);
              const finalPrice = (req.cart_total || 0) - descMonto;
              return (
              <div key={req.id} className="p-3 bg-white rounded-lg border border-amber-200 flex items-center gap-4" data-testid="discount-request-card">
                <div className="flex-1">
                  <p className="font-bold text-slate-800">
                    {req.requested_by} solicita vender en <span className="text-amber-700">₡{finalPrice.toLocaleString("es-CR")}</span>
                  </p>
                  <p className="text-sm text-slate-500">
                    Subtotal ₡{(req.cart_total || 0).toLocaleString("es-CR")} · Descuento ₡{descMonto.toLocaleString("es-CR")} ({req.percentage.toFixed(2)}%)
                  </p>
                  {req.products_summary && <p className="text-xs text-slate-400 truncate">{req.products_summary}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Input 
                    type="number" 
                    placeholder={`${req.percentage.toFixed(2)}%`}
                    value={approvePercentage}
                    onChange={e => setApprovePercentage(e.target.value)}
                    className="w-20 text-center text-sm"
                    data-testid="approve-percentage-input"
                  />
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApproveDiscount(req)} data-testid="approve-discount-btn">
                    <CheckCircle className="w-4 h-4 mr-1" /> Aprobar
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleRejectDiscount(req)} data-testid="reject-discount-btn">
                    Rechazar
                  </Button>
                </div>
              </div>
            );})}
          </CardContent>
        </Card>
      )}

      {showLiveReport && cashRegister && (
        <div className="space-y-3">
          <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Reporte en Tiempo Real - {selectedBox?.name}
          </h2>
          <LiveReport 
            key={`${selectedBox?.id}_${cashRegister?.id}`}
            cashRegister={cashRegister}
            selectedBox={selectedBox}
            pendingVentas={pendingVentas}
            onTransactionCreated={() => {
              fetchCashRegister();
              fetchPendingValues();
            }}
          />
        </div>
      )}

      {isMaster && selectedBox && (
        <DashboardAnalytics boxId={selectedBox.id} />
      )}

      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent data-testid="close-cash-dialog" className="w-full max-w-full h-[100dvh] max-h-[100dvh] rounded-none p-4 pb-0 sm:p-6 sm:max-w-4xl sm:h-auto sm:max-h-[90vh] sm:rounded-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {user.role === 'master' ? 'Cerrar Caja - Revisar y Confirmar' : 'Preparar Cierre - Conteo de Efectivo'}
            </DialogTitle>
            <DialogDescription>
              {user.role === 'master' 
                ? cashRegister.prepared_by 
                  ? `Conteo preparado por ${cashRegister.prepared_by}. Revisa y confirma el cierre.`
                  : 'Revisa los datos y cuenta los billetes y monedas para el cierre final'
                : 'Cuenta los billetes y monedas. PALISTORE revisará y cerrará la caja.'}
            </DialogDescription>
          </DialogHeader>
          
          {(user.role === 'cajero' || (user.role === 'master' && !cashRegister.prepared_by)) && (
            <Tabs defaultValue="colones" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="colones">Colones (₡)</TabsTrigger>
                <TabsTrigger value="dollars">Dólares ($)</TabsTrigger>
              </TabsList>
              
              <TabsContent value="colones" className="mt-4">
                <CashCounter key={`close-colones-${closeDialogKey}`} currency="colones" onTotalChange={handleColonesCloseTotal} />
              </TabsContent>
              
              <TabsContent value="dollars" className="mt-4">
                <CashCounter key={`close-dollars-${closeDialogKey}`} currency="dollars" onTotalChange={handleDollarsCloseTotal} />
              </TabsContent>
            </Tabs>
          )}

          {user.role === 'master' && cashRegister.prepared_by && (
            <div className="p-4 bg-green-50 border-2 border-green-400 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-bold text-green-800">Conteo preparado por {cashRegister.prepared_by}</p>
                  <p className="text-sm text-green-700">
                    {new Date(cashRegister.prepared_at).toLocaleString('es-CR')}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="bg-white p-3 rounded border border-green-300">
                  <p className="text-sm text-green-600 font-semibold">Efectivo Contado (₡)</p>
                  <p className="text-2xl font-bold text-green-700">
                    ₡{closeData.final_cash_colones.toFixed(2)}
                  </p>
                </div>
                <div className="bg-white p-3 rounded border border-green-300">
                  <p className="text-sm text-green-600 font-semibold">Efectivo Contado ($)</p>
                  <p className="text-2xl font-bold text-green-700">
                    ${closeData.final_cash_dollars.toFixed(2)}
                  </p>
                </div>
              </div>
              <p className="text-sm text-green-700 mt-3 text-center">
                ✓ Datos listos para el cierre. Solo ingresa tu contraseña para confirmar.
              </p>
            </div>
          )}

          <div className="space-y-3 pt-4 border-t">
            {(() => {
              const { expectedColones, expectedDollars } = getExpectedCash();
              const diffColones = closeData.final_cash_colones - expectedColones;
              const diffDollars = closeData.final_cash_dollars - expectedDollars;
              const hasDiscrepancy = Math.abs(diffColones) > 0.01 || Math.abs(diffDollars) > 0.01;

              return (
                <>
                  <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div>
                      <p className="text-sm text-blue-600 font-semibold">Efectivo Esperado (₡)</p>
                      <p className="text-xl font-bold text-blue-700">
                        ₡{expectedColones.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-semibold">Efectivo Esperado ($)</p>
                      <p className="text-xl font-bold text-blue-700">
                        ${expectedDollars.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 p-4 bg-slate-100 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-600">Efectivo Contado (₡)</p>
                      <p className="text-xl font-bold text-blue-600" data-testid="close-total-colones">
                        ₡{closeData.final_cash_colones.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Efectivo Contado ($)</p>
                      <p className="text-xl font-bold text-green-600" data-testid="close-total-dollars">
                        ${closeData.final_cash_dollars.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {hasDiscrepancy && (
                    <div className="p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-bold text-yellow-800 mb-2">⚠️ ADVERTENCIA: El efectivo NO coincide</p>
                          <div className="space-y-1 text-sm">
                            {Math.abs(diffColones) > 0.01 && (
                              <p className="text-yellow-700">
                                <span className="font-semibold">Diferencia en Colones:</span> {diffColones >= 0 ? '+' : ''}₡{diffColones.toFixed(2)}
                              </p>
                            )}
                            {Math.abs(diffDollars) > 0.01 && (
                              <p className="text-yellow-700">
                                <span className="font-semibold">Diferencia en Dólares:</span> {diffDollars >= 0 ? '+' : ''}${diffDollars.toFixed(2)}
                              </p>
                            )}
                          </div>
                          <p className="text-yellow-800 font-semibold mt-2">
                            Por favor REVISA el conteo antes de cerrar la caja.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {!hasDiscrepancy && closeData.final_cash_colones > 0 && (
                    <div className="p-4 bg-green-50 border-2 border-green-400 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                        <p className="font-bold text-green-800">✅ El efectivo contado coincide con el esperado</p>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            <div>
              <Label htmlFor="closed_at_display">Fecha y Hora de Cierre</Label>
              <Input
                id="closed_at_display"
                type="text"
                value={closeData.closed_at_display}
                className="mt-1.5 font-semibold text-lg"
                readOnly={user?.role !== "master"}
                onChange={async (e) => {
                  if (user?.role === "master") {
                    // Allow master to edit - but this is just display
                    setCloseData({...closeData, closed_at_display: e.target.value});
                  }
                }}
              />
              {user?.role === "master" && (
                <div className="mt-2">
                  <Label htmlFor="closed_at" className="text-xs text-slate-600">Ajuste fino (formato 24h)</Label>
                  <Input
                    id="closed_at"
                    type="datetime-local"
                    value={closeData.closed_at}
                    onChange={(e) => {
                      const newDatetimeLocal = e.target.value;
                      const formatted = formatDateTime12h(newDatetimeLocal);
                      setCloseData({
                        ...closeData, 
                        closed_at: newDatetimeLocal,
                        closed_at_display: formatted
                      });
                    }}
                    className="mt-1"
                  />
                </div>
              )}
              <p className="text-xs text-slate-500 mt-1">
                {user?.role === "master" 
                  ? "Hora actual de Costa Rica (solo Master puede modificar)" 
                  : "Hora actual de Costa Rica (solo Master puede modificar)"}
              </p>
            </div>

            {/* NEW: Commission fields - ONLY FOR MASTER */}
            {user.role === 'master' && (
            <div className="border-t pt-4 mt-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">💰 Comisiones del Día</h3>
              <p className="text-sm text-slate-600 mb-4">
                Ingresa los montos de comisión que se rebajarán del efectivo antes de cerrar la caja.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="commission_colones">Comisión en Colones (₡)</Label>
                  <Input
                    id="commission_colones"
                    type="number"
                    step="0.01"
                    min="0"
                    value={closeData.commission_colones}
                    onChange={(e) => setCloseData({...closeData, commission_colones: parseFloat(e.target.value) || 0})}
                    className="mt-1.5"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="commission_dollars">Comisión en Dólares ($)</Label>
                  <Input
                    id="commission_dollars"
                    type="number"
                    step="0.01"
                    min="0"
                    value={closeData.commission_dollars}
                    onChange={(e) => setCloseData({...closeData, commission_dollars: parseFloat(e.target.value) || 0})}
                    className="mt-1.5"
                    placeholder="0.00"
                  />
                </div>
              </div>
              {(closeData.commission_colones > 0 || closeData.commission_dollars > 0) && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Efectivo después de comisiones:</strong>
                  </p>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <p className="text-blue-800">
                      ₡{(closeData.final_cash_colones - closeData.commission_colones).toFixed(2)}
                    </p>
                    <p className="text-blue-800">
                      ${(closeData.final_cash_dollars - closeData.commission_dollars).toFixed(2)}
                    </p>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* NEW: SALDO API and SALDO TIRO fields (informational only) - ONLY FOR MASTER */}
            {user.role === 'master' && (
            <div className="border-t pt-4 mt-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">📊 Saldos Informativos</h3>
              <p className="text-sm text-slate-600 mb-4">
                <strong>Solo para reportes</strong> - NO afecta el efectivo de la caja
              </p>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-semibold text-slate-700">SALDO API</Label>
                  <div className="grid grid-cols-2 gap-3 mt-1.5">
                    <div>
                      <Label htmlFor="saldo_api_colones" className="text-xs">Colones (₡)</Label>
                      <Input
                        id="saldo_api_colones"
                        type="number"
                        step="0.01"
                        min="0"
                        value={closeData.saldo_api_colones}
                        onChange={(e) => setCloseData({...closeData, saldo_api_colones: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label htmlFor="saldo_api_dollars" className="text-xs">Dólares ($)</Label>
                      <Input
                        id="saldo_api_dollars"
                        type="number"
                        step="0.01"
                        min="0"
                        value={closeData.saldo_api_dollars}
                        onChange={(e) => setCloseData({...closeData, saldo_api_dollars: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700">SALDO TIRO</Label>
                  <div className="grid grid-cols-2 gap-3 mt-1.5">
                    <div>
                      <Label htmlFor="saldo_tiro_colones" className="text-xs">Colones (₡)</Label>
                      <Input
                        id="saldo_tiro_colones"
                        type="number"
                        step="0.01"
                        min="0"
                        value={closeData.saldo_tiro_colones}
                        onChange={(e) => setCloseData({...closeData, saldo_tiro_colones: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label htmlFor="saldo_tiro_dollars" className="text-xs">Dólares ($)</Label>
                      <Input
                        id="saldo_tiro_dollars"
                        type="number"
                        step="0.01"
                        min="0"
                        value={closeData.saldo_tiro_dollars}
                        onChange={(e) => setCloseData({...closeData, saldo_tiro_dollars: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Option to close both shifts - MASTER only, Turno 1 only */}
            {user.role === 'master' && cashRegister?.turno === 1 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="close_both_shifts"
                    checked={closeData.close_both_shifts}
                    onChange={(e) => setCloseData({...closeData, close_both_shifts: e.target.checked})}
                    className="mt-1 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  />
                  <div>
                    <Label htmlFor="close_both_shifts" className="text-amber-800 font-medium cursor-pointer">
                      🌙 Cerrar Turno 1 + Turno 2 (Fin del día)
                    </Label>
                    <p className="text-xs text-amber-600 mt-1">
                      Marque esta opción si no habrá cambio de turno hoy. Este cierre contará como ambos turnos y no se podrá abrir otra caja hoy.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="close_password">Contraseña Master</Label>
              <Input
                id="close_password"
                data-testid="close-password-input"
                type="password"
                value={closeData.password}
                onChange={(e) => setCloseData({...closeData, password: e.target.value})}
                className="mt-1.5 h-11 sm:h-9"
                placeholder="Ingresa tu contraseña"
              />
            </div>
          </div>
          
          <DialogFooter className="sticky bottom-0 -mx-4 px-4 py-3 bg-white border-t grid grid-cols-2 gap-2 sm:static sm:mx-0 sm:px-0 sm:py-0 sm:border-0 sm:flex sm:gap-0">
            <Button variant="outline" onClick={() => setShowCloseDialog(false)} className="h-12 sm:h-9">
              Cancelar
            </Button>
            <Button
              data-testid="confirm-close-cash-button"
              onClick={handleCloseCashRegister}
              className="bg-red-600 hover:bg-red-700 h-12 sm:h-9"
              disabled={!closeData.password}
            >
              {user.role === 'master' ? 'Confirmar Cierre' : 'Guardar Conteo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmación para anular apertura */}
      <Dialog open={showCancelOpenDialog} onOpenChange={setShowCancelOpenDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-orange-600">❌ Anular Apertura de Caja</DialogTitle>
            <DialogDescription>
              ¿Está seguro de anular esta apertura de caja?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-800 font-semibold mb-2">Esta acción:</p>
              <ul className="text-sm text-orange-700 list-disc list-inside space-y-1">
                <li>Cerrará la caja inmediatamente con fecha de hoy</li>
                <li>Eliminará todas las transacciones de esta apertura</li>
                <li>Permitirá al cajero volver a abrir la caja correctamente</li>
              </ul>
            </div>
            
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-xs text-yellow-800">
                <strong>⚠️ Usar solo si:</strong> El cajero abrió la caja con datos incorrectos 
                (ej: monto inicial equivocado) y necesita volver a empezar.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCancelOpenDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCancelOpening}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Confirmar Anulación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CashOutlayDialog
        open={showOutlayDialog}
        onOpenChange={setShowOutlayDialog}
        box={selectedBox}
        cashRegister={cashRegister}
        user={user}
        onSaved={() => fetchCashRegister()}
      />
    </div>
  );
}